<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import PlatformTooltipIcon from 'dashboard/components-next/PlatformTooltipIcon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import QRCode from 'qrcode';
import { useAlert } from 'dashboard/composables';

const { t } = useI18n();

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const isLoading = ref(false);
const isRestarting = ref(false);
const isLoggingOut = ref(false);
const qrCodeUrl = ref('');
const pairingCode = ref('');
const connectionState = ref(null);
const isCheckingConnection = ref(false);
const connectionCheckTimeout = ref(null);
const webhookConfigured = ref(false);
const postConnectionDone = ref(false);
const pullCommandsSent = ref(false);

// [FEATURE 2026-02-10] WAHA Feature Toggles
const wahaIncludeGroups = ref(false);
const wahaImportContacts = ref(false);
const wahaImportHistory = ref(false);
const wahaRejectCalls = ref(false);
const wahaRejectCallsMessage = ref(
  'Não aceitamos ligações. Por favor, envie uma mensagem de texto.'
);

// Initialize toggles from existing channel additional_attributes
const initToggles = () => {
  const attrs = props.inbox?.channel?.additional_attributes || {};
  wahaIncludeGroups.value = attrs.waha_include_groups || false;
  wahaImportContacts.value = attrs.waha_import_contacts || false;
  wahaImportHistory.value = attrs.waha_import_history || false;
  wahaRejectCalls.value = attrs.waha_reject_calls || false;
  wahaRejectCallsMessage.value =
    attrs.waha_reject_calls_message ||
    'Não aceitamos ligações. Por favor, envie uma mensagem de texto.';
  postConnectionDone.value = attrs.waha_post_connection_done || false;
  pullCommandsSent.value = attrs.waha_pull_commands_sent || false;
};

const generateInstanceName = (inboxName, accountId) => {
  const sanitizedName = inboxName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `account_${accountId}_${sanitizedName}`;
};

const configureWebhook = async () => {
  if (webhookConfigured.value) return;

  try {
    const serverUrlResponse = await InboxesAPI.getServerUrl(props.inbox.id);
    const { server_url: serverUrl, provider } = serverUrlResponse.data;

    // For WAHA, the backend already configures the webhook URL via the ChatWoot app
    if (provider === 'waha') {
      webhookConfigured.value = true;
      return;
    }

    // For Evolution provider, configure webhook URL on the frontend
    const accountId = window.location.pathname.split('/')[3];
    const instanceName = generateInstanceName(props.inbox.name, accountId);
    const webhookUrl = `${serverUrl}/chatwoot/webhook/${encodeURIComponent(instanceName)}`;

    await InboxesAPI.update(props.inbox.id, {
      channel: {
        webhook_url: webhookUrl,
      },
    });

    webhookConfigured.value = true;
    useAlert('Webhook configured successfully');
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to configure webhook');
  }
};

// [FEATURE 2026-02-10] Build toggle payload for backend
const buildWahaToggles = () => ({
  include_groups: wahaIncludeGroups.value,
  import_contacts: wahaImportContacts.value,
  import_history: wahaImportHistory.value,
  reject_calls: wahaRejectCalls.value,
  reject_calls_message: wahaRejectCallsMessage.value,
});

const generateQrcode = async () => {
  isLoading.value = true;
  qrCodeUrl.value = '';
  pairingCode.value = '';

  if (connectionCheckTimeout.value) {
    clearTimeout(connectionCheckTimeout.value);
    connectionCheckTimeout.value = null;
  }

  try {
    // [FEATURE 2026-02-10] Pass WAHA toggles only on first connection
    // On reconnection, settings are already saved — don't resend
    const wahaToggles = isFirstConnection.value ? buildWahaToggles() : null;
    const response = await InboxesAPI.generateQrcode(
      props.inbox.id,
      wahaToggles
    );
    const { code, pairingCode: codePairing } = response.data;

    if (code) {
      const qrCodeDataUrl = await QRCode.toDataURL(code, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF',
        },
      });
      qrCodeUrl.value = qrCodeDataUrl;
      pairingCode.value = codePairing || '';
      useAlert(t('INBOX_MGMT.QRCODE_CONNECTOR.QR_GENERATED_SUCCESS'));

      // Schedule connection check after 20 seconds
      connectionCheckTimeout.value = setTimeout(async () => {
        await checkConnection();
        if (getConnectionStatus.value === 'connected') {
          qrCodeUrl.value = '';
          pairingCode.value = '';
        }
      }, 20000);
    }
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to generate QR code');
  } finally {
    isLoading.value = false;
  }
};

const restartInstance = async () => {
  isRestarting.value = true;

  try {
    await InboxesAPI.restartInstance(props.inbox.id);
    useAlert('Instance restarted successfully');
    checkConnection();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to restart instance');
  } finally {
    isRestarting.value = false;
  }
};

const logoutInstance = async () => {
  isLoggingOut.value = true;

  try {
    await InboxesAPI.logoutInstance(props.inbox.id);
    useAlert('Instance logged out successfully');
    checkConnection();
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to logout instance');
  } finally {
    isLoggingOut.value = false;
  }
};

// [FEATURE 2026-02-10] Trigger import jobs after connection is established
const triggerPostConnectionSetup = async () => {
  if (postConnectionDone.value) return;

  try {
    const response = await InboxesAPI.postConnectionSetup(props.inbox.id);
    const {
      jobs_dispatched: jobs,
      skipped,
      pull_commands_sent: pullSent,
    } = response.data;

    if (skipped) {
      postConnectionDone.value = true;
      // [FIX 2026-02-10] Even when skipped, check if pull was already sent
      // (reconnection case — flag is already true on the server)
      if (pullSent) {
        pullCommandsSent.value = true;
      }
      return;
    }

    postConnectionDone.value = true;

    // [FIX 2026-02-10] Update local state so UI switches to read-only badges immediately
    if (pullSent) {
      pullCommandsSent.value = true;
    }

    if (jobs && jobs.length > 0) {
      useAlert(t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_STARTED'));
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[WAHA] Post-connection setup error:', error);
  }
};

const checkConnection = async () => {
  isCheckingConnection.value = true;

  try {
    const response = await InboxesAPI.getConnectionState(props.inbox.id);
    const state =
      response.data?.state ||
      response.data?.instance?.state ||
      response.data?.connectionState;
    connectionState.value = state;

    const status = state?.toLowerCase();
    if (status === 'open' || status === 'connected') {
      qrCodeUrl.value = '';
      pairingCode.value = '';
      if (connectionCheckTimeout.value) {
        clearTimeout(connectionCheckTimeout.value);
        connectionCheckTimeout.value = null;
      }

      // Configure webhook when connection is established
      await configureWebhook();

      // [FEATURE 2026-02-10] Trigger post-connection import jobs
      await triggerPostConnectionSetup();
    }
  } catch (error) {
    connectionState.value = 'close';
  } finally {
    isCheckingConnection.value = false;
  }
};

const getConnectionStatus = computed(() => {
  if (!connectionState.value) return null;
  const state = connectionState.value.toLowerCase();
  if (state === 'open' || state === 'connected') return 'connected';
  if (state === 'connecting') return 'connecting';
  return 'disconnected';
});

const getStatusColor = computed(() => {
  const status = getConnectionStatus.value;
  if (status === 'connected') return 'bg-green-500';
  if (status === 'connecting') return 'bg-yellow-500';
  return 'bg-red-500';
});

const getStatusText = computed(() => {
  const status = getConnectionStatus.value;
  if (status === 'connected')
    return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTED') || 'Connected';
  if (status === 'connecting')
    return t('INBOX_MGMT.QRCODE_CONNECTOR.CONNECTING') || 'Connecting';
  return t('INBOX_MGMT.QRCODE_CONNECTOR.DISCONNECTED') || 'Disconnected';
});

// [FIX 2026-02-10] Show editable checkboxes ONLY on first connection
// After first import (waha_pull_commands_sent = true), show read-only badges instead
// [FIX 2026-02-18] Also check postConnectionDone — pullCommandsSent depends on a delayed job
// (30s PullMessagesJob) which may not have run yet. postConnectionDone is set immediately
// by the backend in post_connection_setup, making it a reliable guard against the
// intermittent bug where checkboxes persist after successful QR code connection.
const isFirstConnection = computed(
  () => !pullCommandsSent.value && !postConnectionDone.value
);

const showToggles = computed(() => {
  return isFirstConnection.value;
});

// Read-only saved settings for display after first connection
// [FIX 2026-02-11] Uses LOCAL refs (not props) because props.inbox doesn't refresh
// after save_waha_toggle_preferences saves to DB — Vuex store is stale.
const savedSettings = computed(() => {
  return [
    {
      key: 'include_groups',
      label: t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.INCLUDE_GROUPS'),
      enabled: wahaIncludeGroups.value,
    },
    {
      key: 'import_contacts',
      label: t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_CONTACTS'),
      enabled: wahaImportContacts.value,
    },
    {
      key: 'import_history',
      label: t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_HISTORY'),
      enabled: wahaImportHistory.value,
    },
    {
      key: 'reject_calls',
      label: t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.REJECT_CALLS'),
      enabled: wahaRejectCalls.value,
    },
  ];
});

onMounted(() => {
  initToggles();
  checkConnection();
});

// [DEEP REVIEW FIX 2026-02-10] Clear pending timeout on component unmount to prevent
// leaked timers and potential errors when the component is destroyed mid-check
onBeforeUnmount(() => {
  if (connectionCheckTimeout.value) {
    clearTimeout(connectionCheckTimeout.value);
    connectionCheckTimeout.value = null;
  }
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.TABS.QRCODE_CONNECTOR')"
      :sub-title="$t('INBOX_MGMT.QRCODE_CONNECTOR.SUB_TITLE')"
    >
      <div class="flex items-center gap-1 mb-4">
        <PlatformTooltipIcon identifier="inbox_qrcode_connector" size="sm" />
      </div>
      <div class="flex flex-col gap-6">
        <div
          v-if="connectionState !== null"
          class="flex items-center gap-2 mb-2"
        >
          <div class="w-3 h-3 rounded-full" :class="getStatusColor" />
          <span class="text-sm text-slate-600 dark:text-slate-400">
            {{ getStatusText }}
          </span>
        </div>

        <!-- [FEATURE 2026-02-10] WAHA Feature Toggles — First Connection: Editable -->
        <div
          v-if="showToggles"
          class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5"
        >
          <h4
            class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4"
          >
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.TITLE') }}
          </h4>

          <div class="flex flex-col gap-4">
            <!-- Include Groups Toggle -->
            <label
              class="flex items-center gap-3 cursor-pointer"
              for="waha-include-groups"
            >
              <input
                id="waha-include-groups"
                v-model="wahaIncludeGroups"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
              />
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.INCLUDE_GROUPS') }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t(
                      'INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.INCLUDE_GROUPS_DESC'
                    )
                  }}
                </span>
              </div>
            </label>

            <!-- Import Contacts Toggle -->
            <label
              class="flex items-center gap-3 cursor-pointer"
              for="waha-import-contacts"
            >
              <input
                id="waha-import-contacts"
                v-model="wahaImportContacts"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
              />
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{
                    $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_CONTACTS')
                  }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t(
                      'INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_CONTACTS_DESC'
                    )
                  }}
                </span>
              </div>
            </label>

            <!-- Import History Toggle -->
            <label
              class="flex items-center gap-3 cursor-pointer"
              for="waha-import-history"
            >
              <input
                id="waha-import-history"
                v-model="wahaImportHistory"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
              />
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_HISTORY') }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t(
                      'INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.IMPORT_HISTORY_DESC'
                    )
                  }}
                </span>
              </div>
            </label>

            <!-- Reject Calls Toggle -->
            <label
              class="flex items-center gap-3 cursor-pointer"
              for="waha-reject-calls"
            >
              <input
                id="waha-reject-calls"
                v-model="wahaRejectCalls"
                type="checkbox"
                class="w-4 h-4 rounded border-slate-300 dark:border-slate-600 text-woot-500 focus:ring-woot-500"
              />
              <div class="flex flex-col">
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.REJECT_CALLS') }}
                </span>
                <span class="text-xs text-slate-500 dark:text-slate-400">
                  {{
                    $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.REJECT_CALLS_DESC')
                  }}
                </span>
              </div>
            </label>

            <!-- Reject Calls Message (visible when Reject Calls is enabled) -->
            <div v-if="wahaRejectCalls" class="ml-7">
              <label
                class="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1 block"
                for="waha-reject-message"
              >
                {{
                  $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.REJECT_MESSAGE_LABEL')
                }}
              </label>
              <input
                id="waha-reject-message"
                v-model="wahaRejectCallsMessage"
                type="text"
                class="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 focus:ring-woot-500 focus:border-woot-500"
                :placeholder="
                  $t(
                    'INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.REJECT_MESSAGE_PLACEHOLDER'
                  )
                "
              />
            </div>
          </div>
        </div>
        <!-- End WAHA Feature Toggles -->

        <!-- [FEATURE 2026-02-10] Saved Settings — Read-only badges after first connection -->
        <div
          v-if="!isFirstConnection"
          class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5"
        >
          <h4
            class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1"
          >
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.SAVED_SETTINGS_TITLE') }}
          </h4>
          <p class="text-xs text-slate-500 dark:text-slate-400 mb-4">
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.TOGGLES.SAVED_SETTINGS_DESC') }}
          </p>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="setting in savedSettings"
              :key="setting.key"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
              :class="
                setting.enabled
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400'
              "
            >
              <span>{{ setting.enabled ? '✅' : '❌' }}</span>
              {{ setting.label }}
            </span>
          </div>
        </div>
        <!-- End Saved Settings -->

        <div class="flex justify-start items-center gap-3">
          <NextButton :is-loading="isLoading" @click="generateQrcode">
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.GENERATE_BUTTON') }}
          </NextButton>
          <NextButton
            color="amber"
            :is-loading="isRestarting"
            @click="restartInstance"
          >
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.RESTART_BUTTON') }}
          </NextButton>
          <NextButton
            color="ruby"
            :is-loading="isLoggingOut"
            @click="logoutInstance"
          >
            {{ $t('INBOX_MGMT.QRCODE_CONNECTOR.LOGOUT_BUTTON') }}
          </NextButton>
        </div>

        <div v-if="qrCodeUrl" class="flex flex-col items-center gap-6 mt-2">
          <div class="w-full max-w-md text-center">
            <p class="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {{
                $t('INBOX_MGMT.QRCODE_CONNECTOR.SCAN_INSTRUCTION') ||
                'Scan this QR code with your device to connect'
              }}
            </p>
          </div>
          <div
            class="bg-white dark:bg-slate-800 p-8 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <div
              class="flex items-center justify-center bg-white dark:bg-slate-900 p-4 rounded-lg"
            >
              <img :src="qrCodeUrl" alt="QR Code" class="w-64 h-64" />
            </div>
          </div>

          <div v-if="pairingCode" class="w-full max-w-md">
            <div
              class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4"
            >
              <p
                class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
              >
                {{
                  $t('INBOX_MGMT.QRCODE_CONNECTOR.PAIRING_CODE_LABEL') ||
                  'Pairing Code'
                }}
              </p>
              <div class="flex items-center gap-2">
                <code
                  class="flex-1 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md text-sm font-mono text-slate-900 dark:text-slate-100 break-all"
                >
                  {{ pairingCode }}
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
